import React, { useState } from "react";
import {
  Trash, Plus, FlaskConical, Link as LinkIcon, Unlink, ChevronDown, ChevronRight, Sparkles,
} from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";

const KIND_OPTIONS = [
  { value: "process", label: "Process (no cost link)" },
  { value: "ingredient_quantity", label: "Ingredient quantity" },
  { value: "ingredient_rate", label: "Ingredient rate ₹/kg" },
  { value: "utility", label: "Utility cost" },
  { value: "overhead", label: "Overhead cost" },
];
const MODE_OPTIONS = [
  { value: "absolute", label: "Replace value" },
  { value: "delta", label: "Add to base" },
  { value: "scale", label: "Multiply base" },
];
const UTILITY_KEYS = ["gas", "electricity", "water", "packaging", "delivery"];
const OVERHEAD_KEYS = ["labour", "rent", "kitchen_overhead", "maintenance", "misc"];

// ---------------------------------------------------------------------------
// Categorized factor templates (preset library)
// Each preset seeds: name, unit, min, max, step
// PMF presets default to 1-10 Likert scoring.
// ---------------------------------------------------------------------------
const PRESET_GROUPS = [
  {
    label: "Ingredient",
    items: [
      { id: "ing.qty", name: "Ingredient Quantity", unit: "g", min: 0, max: 100, step: 5 },
      { id: "ing.ratio", name: "Substitution Ratio", unit: "%", min: 0, max: 100, step: 5 },
      { id: "ing.rate", name: "Ingredient Rate", unit: "₹/kg", min: 50, max: 800, step: 10 },
      { id: "ing.fat", name: "Fat Content", unit: "%", min: 0, max: 30, step: 1 },
      { id: "ing.sugar", name: "Sugar Content", unit: "%", min: 0, max: 25, step: 1 },
      { id: "ing.protein", name: "Protein Content", unit: "g", min: 0, max: 40, step: 1 },
    ],
  },
  {
    label: "Process",
    items: [
      { id: "proc.mix.time", name: "Mixing Time", unit: "min", min: 1, max: 30, step: 1 },
      { id: "proc.cook.temp", name: "Cooking Temperature", unit: "°C", min: 120, max: 240, step: 5 },
      { id: "proc.cook.time", name: "Cooking Time", unit: "min", min: 5, max: 60, step: 1 },
      { id: "proc.proof", name: "Proofing Time", unit: "min", min: 30, max: 240, step: 10 },
      { id: "proc.hydration", name: "Dough Hydration", unit: "%", min: 50, max: 90, step: 2 },
      { id: "proc.ph", name: "pH", unit: "pH", min: 3.5, max: 7.0, step: 0.1 },
      { id: "proc.yeast", name: "Yeast Dose", unit: "%", min: 0.5, max: 3.0, step: 0.25 },
      { id: "proc.salt", name: "Salt Level", unit: "%", min: 0.5, max: 2.5, step: 0.1 },
    ],
  },
  {
    label: "Cost",
    items: [
      { id: "cost.target", name: "Cost Target / Portion", unit: "₹", min: 0, max: 500, step: 5 },
      { id: "cost.utility", name: "Utility Cost", unit: "₹", min: 0, max: 100, step: 1 },
      { id: "cost.packaging", name: "Packaging Cost", unit: "₹", min: 0, max: 50, step: 1 },
      { id: "cost.margin", name: "Target Margin", unit: "%", min: 10, max: 60, step: 1 },
    ],
  },
  {
    label: "Sensory",
    items: [
      { id: "sen.taste", name: "Taste", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "sen.crisp", name: "Crispness", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "sen.aroma", name: "Aroma", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "sen.sweet", name: "Sweetness", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "sen.salt", name: "Saltiness", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "sen.spice", name: "Spiciness", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "sen.color", name: "Color Intensity", unit: "1-10", min: 1, max: 10, step: 1 },
    ],
  },
  {
    label: "Product-Market Fit (1-10)",
    items: [
      { id: "pmf.price", name: "Price", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "pmf.performance", name: "Performance", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "pmf.quality", name: "Quality", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "pmf.functionality", name: "Functionality", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "pmf.ergonomics", name: "Ergonomics", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "pmf.manufacturability", name: "Manufacturability", unit: "1-10", min: 1, max: 10, step: 1 },
      { id: "pmf.marketability", name: "Marketability", unit: "1-10", min: 1, max: 10, step: 1 },
    ],
  },
];

const findPreset = (id) => {
  for (const g of PRESET_GROUPS) {
    const m = g.items.find((it) => it.id === id);
    if (m) return m;
  }
  return null;
};

export default function FactorEditor({ factors, onChange, baseRecipe }) {
  // First factor is open by default; track per-id open state.
  const [openMap, setOpenMap] = useState(() => {
    const m = {};
    (factors || []).forEach((f, i) => { m[f.id] = i === 0; });
    return m;
  });
  const isOpen = (id) => openMap[id] !== false;
  const toggleOpen = (id) => setOpenMap((m) => ({ ...m, [id]: !isOpen(id) }));

  const ingredients = [
    ...((baseRecipe?.primary_ingredients) || []).map((i) => ({ ...i, group: "primary" })),
    ...((baseRecipe?.secondary_ingredients) || []).map((i) => ({ ...i, group: "secondary" })),
  ];

  const update = (idx, patch) => {
    const next = [...factors]; next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const updateBinding = (idx, patch) => {
    const next = [...factors];
    const b = { target_kind: "process", target_id: null, mode: "absolute", ...(next[idx].binding || {}), ...patch };
    next[idx] = { ...next[idx], binding: b };
    onChange(next);
  };
  const remove = (idx) => onChange(factors.filter((_, i) => i !== idx));
  const applyPreset = (idx, presetId) => {
    if (!presetId || presetId === "__none") return;
    const p = findPreset(presetId);
    if (!p) return;
    update(idx, { name: p.name, unit: p.unit, min_value: p.min, max_value: p.max, step: p.step });
  };

  const add = () => {
    if (factors.length >= 6) return;
    const id = crypto.randomUUID();
    onChange([
      ...factors,
      {
        id,
        name: `Factor ${factors.length + 1}`,
        unit: "",
        min_value: 0,
        max_value: 10,
        step: 1,
        binding: { target_kind: "process", target_id: null, mode: "absolute" },
      },
    ]);
    setOpenMap((m) => ({ ...m, [id]: true }));
  };

  return (
    <div className="space-y-4" data-testid="rnd-factor-editor">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="kostra-section-title text-lg">Independent Variables (Factors)</h3>
          <p className="text-xs text-[#4B5563] mt-1 max-w-xl">
            Define 2 to 6 factors. Pick from the preset library (Ingredient, Process, Cost, Sensory or
            Product-Market Fit) or define your own. Each factor can optionally bind to a recipe ingredient
            or utility for live cost integration.
          </p>
        </div>
        <Button
          type="button"
          onClick={add}
          disabled={factors.length >= 6}
          className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold shrink-0"
          data-testid="rnd-add-factor"
        >
          <Plus size={16} className="mr-1.5" /> Add Factor
        </Button>
      </div>

      {factors.length === 0 ? (
        <div className="rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-10 text-center">
          <FlaskConical size={28} className="mx-auto text-[#0A3B24]" />
          <div className="text-sm font-semibold text-[#111827] mt-2">No factors yet</div>
          <div className="text-xs text-[#4B5563] mt-1">Add at least 2 factors to enable design generation.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {factors.map((f, i) => {
            const b = f.binding || {};
            const showIngredientPicker = b.target_kind === "ingredient_quantity" || b.target_kind === "ingredient_rate";
            const showUtilityKey = b.target_kind === "utility";
            const showOverheadKey = b.target_kind === "overhead";
            const opened = isOpen(f.id);
            const linked = b.target_kind && b.target_kind !== "process";
            return (
              <div
                key={f.id}
                className="rounded-xl border border-[#E5E7EB] bg-white shadow-sm overflow-hidden"
                data-testid={`rnd-factor-${i}`}
              >
                {/* HEADER ROW */}
                <button
                  type="button"
                  onClick={() => toggleOpen(f.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#FDF9EE] to-white hover:from-[#FCF1D6] transition-colors text-left"
                  data-testid={`rnd-factor-toggle-${i}`}
                >
                  {opened
                    ? <ChevronDown size={16} className="text-[#0A3B24] shrink-0" />
                    : <ChevronRight size={16} className="text-[#0A3B24] shrink-0" />}
                  <span className="kostra-data-label !text-[10px] !text-[#4B5563]">FACTOR {i + 1}</span>
                  <span className="font-display text-base text-[#0A3B24] truncate">{f.name || `Factor ${i + 1}`}</span>
                  <span className="hidden md:inline text-[11px] text-[#4B5563] font-mono-num">
                    [{f.min_value} – {f.max_value}{f.unit ? ` ${f.unit}` : ""}]
                  </span>
                  {linked && (
                    <span className="gold-pill text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                      <LinkIcon size={10} /> {b.target_kind.replace("_", " ")}
                    </span>
                  )}
                  <span className="ml-auto inline-flex items-center gap-1">
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); remove(i); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); remove(i); } }}
                      className="h-8 w-8 rounded-md text-[#4B5563] hover:bg-[#FBE8E8] hover:text-[#DC2626] inline-flex items-center justify-center cursor-pointer"
                      aria-label={`Remove factor ${i + 1}`}
                      data-testid={`rnd-factor-remove-${i}`}
                    >
                      <Trash size={15} />
                    </span>
                  </span>
                </button>

                {opened && (
                  <div className="p-5 border-t border-[#F1E9D0] space-y-5">
                    {/* PRESET PICKER */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                      <div className="md:col-span-12">
                        <Label className="kostra-data-label mb-1 flex items-center gap-1.5">
                          <Sparkles size={11} className="text-[#D4AF37]" />
                          Use a Template
                        </Label>
                        <Select onValueChange={(v) => applyPreset(i, v)}>
                          <SelectTrigger className="h-10" data-testid={`rnd-factor-preset-${i}`}>
                            <SelectValue placeholder="Pick a preset (Ingredient, Process, Cost, Sensory, PMF)..." />
                          </SelectTrigger>
                          <SelectContent className="max-h-80">
                            {PRESET_GROUPS.map((g, gi) => (
                              <React.Fragment key={g.label}>
                                {gi > 0 && <SelectSeparator />}
                                <SelectGroup>
                                  <SelectLabel className="text-[#0A3B24] font-semibold">{g.label}</SelectLabel>
                                  {g.items.map((it) => (
                                    <SelectItem key={it.id} value={it.id}>
                                      <span className="font-medium">{it.name}</span>
                                      <span className="text-[10px] text-[#4B5563] ml-2 font-mono-num">
                                        [{it.min}–{it.max} {it.unit}]
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </React.Fragment>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* MAIN VARIABLE GRID — non-overlapping CSS grid */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-3">
                      <div className="md:col-span-5">
                        <Label className="kostra-data-label mb-1 block">Variable Name</Label>
                        <Input
                          value={f.name}
                          onChange={(e) => update(i, { name: e.target.value })}
                          className="h-10"
                          data-testid={`rnd-factor-name-${i}`}
                        />
                      </div>
                      <div className="md:col-span-3">
                        <Label className="kostra-data-label mb-1 block">Unit</Label>
                        <Input
                          value={f.unit}
                          onChange={(e) => update(i, { unit: e.target.value })}
                          placeholder="%, g, °C, min, 1-10"
                          className="h-10"
                          data-testid={`rnd-factor-unit-${i}`}
                        />
                      </div>
                      <div className="md:col-span-4">
                        <Label className="kostra-data-label mb-1 block">Step</Label>
                        <Input
                          type="number"
                          value={f.step}
                          onChange={(e) => update(i, { step: parseFloat(e.target.value || 0) })}
                          className="h-10 font-mono-num"
                          data-testid={`rnd-factor-step-${i}`}
                        />
                      </div>
                      <div className="md:col-span-6">
                        <Label className="kostra-data-label mb-1 block">Min Value</Label>
                        <Input
                          type="number"
                          value={f.min_value}
                          onChange={(e) => update(i, { min_value: parseFloat(e.target.value || 0) })}
                          className="h-10 font-mono-num"
                          data-testid={`rnd-factor-min-${i}`}
                        />
                      </div>
                      <div className="md:col-span-6">
                        <Label className="kostra-data-label mb-1 block">Max Value</Label>
                        <Input
                          type="number"
                          value={f.max_value}
                          onChange={(e) => update(i, { max_value: parseFloat(e.target.value || 0) })}
                          className="h-10 font-mono-num"
                          data-testid={`rnd-factor-max-${i}`}
                        />
                      </div>
                    </div>

                    {/* COST LINK SECTION */}
                    <div className="rounded-lg border border-dashed border-[#E5E7EB] bg-[#F9FAFB] p-4">
                      <div className="kostra-data-label mb-2 flex items-center gap-1.5">
                        {linked
                          ? <LinkIcon size={11} className="text-[#D4AF37]" />
                          : <Unlink size={11} className="text-[#4B5563]" />}
                        Cost Linkage
                        <span className="text-[10px] font-normal normal-case tracking-normal text-[#4B5563] ml-1">
                          (optional — link to a recipe ingredient/utility for live costing)
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                        <div className="md:col-span-4">
                          <Label className="kostra-data-label mb-1 block">Link Type</Label>
                          <Select value={b.target_kind || "process"} onValueChange={(v) => updateBinding(i, { target_kind: v, target_id: null })}>
                            <SelectTrigger className="h-10" data-testid={`rnd-factor-kind-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {KIND_OPTIONS.map((k) => (
                                <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {showIngredientPicker && (
                          <div className="md:col-span-5">
                            <Label className="kostra-data-label mb-1 block">Ingredient</Label>
                            <Select value={b.target_id || ""} onValueChange={(v) => updateBinding(i, { target_id: v })}>
                              <SelectTrigger className="h-10"><SelectValue placeholder="Pick an ingredient" /></SelectTrigger>
                              <SelectContent>
                                {ingredients.length === 0 && (
                                  <SelectItem value="__none" disabled>Attach a base recipe first</SelectItem>
                                )}
                                {ingredients.map((ing) => (
                                  <SelectItem key={ing.id} value={ing.id}>
                                    {ing.group === "primary" ? "P · " : "S · "}{ing.name || "(unnamed)"}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {(showUtilityKey || showOverheadKey) && (
                          <div className="md:col-span-5">
                            <Label className="kostra-data-label mb-1 block">{showUtilityKey ? "Utility key" : "Overhead key"}</Label>
                            <Select value={b.target_id || ""} onValueChange={(v) => updateBinding(i, { target_id: v })}>
                              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {(showUtilityKey ? UTILITY_KEYS : OVERHEAD_KEYS).map((k) => (
                                  <SelectItem key={k} value={k}>{k}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {linked && (
                          <div className="md:col-span-3">
                            <Label className="kostra-data-label mb-1 block">Apply Mode</Label>
                            <Select value={b.mode || "absolute"} onValueChange={(v) => updateBinding(i, { mode: v })}>
                              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {MODE_OPTIONS.map((m) => (
                                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
